import PropTypes from 'prop-types';
import React, { Fragment } from 'react';
import styled from 'styled-components';

import ChoiceChip from '../../choice-chip';
import MathContent from '../../math-content';
import Textarea from '../../textarea';
import { useWindowSize } from '../../../lib/hooks';

import { CORRECT, deleteHtmlTagsFromString, getCharByElementPosition, parseTextareaAnswer } from '../../../lib/exercise';
import Exercise from '../../exercise';

const ExerciseContainer = styled.span`
    margin-left: 32px;
`;

const Container = styled.div`
    display: flex;
    flex-direction: column;
`;

const FillBlanksContainer = styled.div`
    display: flex;
    flex-direction: column;
`;

const FeedbackContainer = styled.div`
    margin-left: 34px;

    ${(props) => props.theme.mediaQueries.desktop} {
        margin-left: 32px;
    }
`;

const TextareaContainer = styled.div``;

const Divider = styled.div`
    margin: 16px;
`;

const Label = styled.span`
    display: inline-block;
    font-weight: 600;
    margin-right: 4px;
    margin-top: 8px;
`;

export const Feedback = styled.span`
    font-size: 16px;
    line-height: 24px;
    letter-spacing: 0;
    margin-top: 8px;
`;

export const Correction = styled.div`
    display: flex;
    flex-direction: column;
`;

const CorrectChoiceChip = styled.span`
    margin-top: 8px;
`;

export const getLabel = (missedAnswer, correct, solution, feedback) => {
    if (solution) {
        if (feedback) {
            return 'Feedback specifico. ';
        }
        return '';
    }
    if (missedAnswer) {
        return 'Risposta non data. ';
    }

    return correct ? 'Risposta corretta. ' : 'Risposta sbagliata. ';
};

export const SingleFillBlanksCorrection = ({ id, correct, correctAnswer, feedback, index, missedAnswer, solution, text, parse }) => {
    const { innerWidth } = useWindowSize();
    return (
        <>
            <TextareaContainer>
                <Textarea
                    badgeIndex={getCharByElementPosition(index + 1)}
                    id={id}
                    isValidated={true}
                    validationType={missedAnswer ? 'missing' : correct ? 'success' : 'error'}
                    value={text ? text : correctAnswer}
                    parse={parse}
                />
            </TextareaContainer>
            <FeedbackContainer isMobile={innerWidth < 730}>
                <Feedback>
                    <b>{getLabel(missedAnswer, correct, solution, feedback)}</b>
                    {feedback && <MathContent style={{ display: 'inline' }}>{feedback}</MathContent>}
                </Feedback>

                {!correct && !missedAnswer && (
                    <Correction>
                        <Label>{'La risposta esatta è:'}</Label>
                        <CorrectChoiceChip>
                            <ChoiceChip id={`${index}`} type="validated" value={correctAnswer} disabled={true} validationType="missing" />
                        </CorrectChoiceChip>
                    </Correction>
                )}
            </FeedbackContainer>
        </>
    );
};

SingleFillBlanksCorrection.propTypes = {
    id: PropTypes.string,
    correct: PropTypes.bool,
    correctAnswer: PropTypes.string,
    feedback: PropTypes.string,
    index: PropTypes.number,
    missedAnswer: PropTypes.bool,
    parse: PropTypes.bool,
    solution: PropTypes.bool,
    text: PropTypes.string,
};

const FillBlanksCorrection = ({ exercise, answers, solution }) => {
    const corrections = exercise.options.map((option) => {
        const answer = answers ? answers.answer.find((item) => item.gapId === option.id) : null;

        if (!answer) {
            return {
                ...option,
                insertedValue: '',
                correctValue: option.values[0],
                missedAnswer: true,
                correct: false,
            };
        }

        const parsedCorrectValues = option.values.map((value) => deleteHtmlTagsFromString(value).trim());
        const parsedAnswer = deleteHtmlTagsFromString(answer.value);

        return {
            ...option,
            correctValue: option.values[0],
            insertedValue: answer.value,
            correct: parsedCorrectValues.some((correctValue) => parseTextareaAnswer(parsedAnswer) === parseTextareaAnswer(correctValue)),
            missedAnswer: false,
        };
    });

    return (
        <Container>
            <ExerciseContainer>
                <Exercise
                    answers={answers ? answers.answer : []}
                    exercise={{ ...exercise, showCorrection: true }}
                    homeworkTerminated={true}
                    showAttachments={false}
                    showSharedText={false}
                    showFeedbacks={false}
                    showActionsBar={false}
                    showTitle={false}
                    correctionStatus={{
                        exerciseId: exercise.id,
                        status: CORRECT,
                    }}
                    showHintBox={false}
                />
            </ExerciseContainer>
            <FillBlanksContainer>
                {corrections.map(
                    (option, index) =>
                        option && (
                            <Fragment key={option.id}>
                                <SingleFillBlanksCorrection
                                    key={option.id}
                                    id={`${option.id}`}
                                    index={index}
                                    missedAnswer={option.missedAnswer}
                                    text={option.insertedValue}
                                    correct={option.correct}
                                    feedback={option.feedback}
                                    correctAnswer={option.correctValue}
                                    solution={solution}
                                />
                                {index < exercise.options.length - 1 && <Divider />}
                            </Fragment>
                        ),
                )}
            </FillBlanksContainer>
        </Container>
    );
};

FillBlanksCorrection.propTypes = {
    answers: PropTypes.shape({
        exerciseId: PropTypes.number,
        answer: PropTypes.array,
    }),
    exercise: PropTypes.object,
    solution: PropTypes.bool,
};

export default FillBlanksCorrection;
